import React, { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronLeft, ChevronRight, UnfoldHorizontal, ZoomIn, ZoomOut } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { Button } from './ui/button';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';
import { cn } from '@/lib/utils';
import {
  TIMELINE_ZOOM_STEP,
  getTimelineNavigationAvailability,
  shiftTimelineWindow,
  unavailableTimelineNavigation,
  type TimelineNavigationApi,
  type TimelineNavigationAvailability,
  type TimelineNavigationBounds,
  type TimelinePanDirection,
} from '../utils/timeline-navigation';

interface TimelineNavButtonProps {
  label: string;
  onClick: () => void;
  icon: LucideIcon;
  disabled: boolean;
  tooltipSide?: 'top' | 'bottom';
}

const TimelineNavButton: React.FC<TimelineNavButtonProps> = ({
  label,
  onClick,
  icon: Icon,
  disabled,
  tooltipSide = 'bottom',
}) => (
  <ButtonTooltip tooltip={label} side={tooltipSide}>
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-[30px] w-[30px] rounded-md text-muted-foreground"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" />
    </Button>
  </ButtonTooltip>
);

interface TimelineNavigationControlsProps {
  timelineRef: { current: TimelineNavigationApi | null };
  visibleWindow: { start: string; end: string } | null;
  bounds: TimelineNavigationBounds | null;
  className?: string;
  overlay?: boolean;
}

function itemRangeFromTimeline(
  timeline: TimelineNavigationApi | null
): { start: Date; end: Date } | null {
  if (!timeline) return null;
  const range = timeline.getItemRange();
  if (!range?.min || !range?.max) return null;
  return { start: range.min, end: range.max };
}

export const TimelineNavigationControls: React.FC<TimelineNavigationControlsProps> = ({
  timelineRef,
  visibleWindow,
  bounds,
  className,
  overlay = false,
}) => {
  const { t } = useLocale();
  const tooltipSide = overlay ? 'top' : 'bottom';

  const availability: TimelineNavigationAvailability = useMemo(() => {
    if (!visibleWindow || !bounds) {
      return unavailableTimelineNavigation();
    }
    return getTimelineNavigationAvailability({
      current: {
        start: new Date(visibleWindow.start),
        end: new Date(visibleWindow.end),
      },
      bounds,
      itemRange: itemRangeFromTimeline(timelineRef.current),
    });
  }, [visibleWindow, bounds, timelineRef]);

  const pan = (direction: TimelinePanDirection): void => {
    const timeline = timelineRef.current;
    if (!timeline) return;
    const next = shiftTimelineWindow(timeline.getWindow(), direction);
    timeline.setWindow(next.start, next.end);
  };

  const zoomIn = (): void => {
    timelineRef.current?.zoomIn(TIMELINE_ZOOM_STEP);
  };

  const zoomOut = (): void => {
    timelineRef.current?.zoomOut(TIMELINE_ZOOM_STEP);
  };

  const fit = (): void => {
    timelineRef.current?.fit();
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          'flex justify-center',
          overlay && 'pointer-events-none absolute inset-x-0 bottom-0 z-10 translate-y-[14px]',
          className
        )}
        role="toolbar"
        aria-label={t('timeline_navigation')}
      >
        <div
          className={cn(
            'inline-flex h-[38px] items-center gap-1 rounded-[8px] border border-border bg-muted/95 p-1 shadow-sm backdrop-blur-sm',
            overlay && 'pointer-events-auto'
          )}
        >
          <TimelineNavButton
            label={t('timeline_pan_left')}
            onClick={() => pan('left')}
            icon={ChevronLeft}
            disabled={!availability.panLeft}
            tooltipSide={tooltipSide}
          />
          <TimelineNavButton
            label={t('timeline_zoom_out')}
            onClick={zoomOut}
            icon={ZoomOut}
            disabled={!availability.zoomOut}
            tooltipSide={tooltipSide}
          />
          <TimelineNavButton
            label={t('timeline_zoom_in')}
            onClick={zoomIn}
            icon={ZoomIn}
            disabled={!availability.zoomIn}
            tooltipSide={tooltipSide}
          />
          <TimelineNavButton
            label={t('timeline_pan_right')}
            onClick={() => pan('right')}
            icon={ChevronRight}
            disabled={!availability.panRight}
            tooltipSide={tooltipSide}
          />
          <div className="mx-0.5 h-4 w-px bg-border" aria-hidden="true" />
          <TimelineNavButton
            label={t('timeline_fit')}
            onClick={fit}
            icon={UnfoldHorizontal}
            disabled={!availability.fit}
            tooltipSide={tooltipSide}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};
